import type { OutboundBeaconCommand } from '../types/analytics';
import type { ClockPort } from '../types/clock';
import type { CoordinatorEventLogEntry } from './audit-types';

type OutboundCommandQueueDependencies = {
  clock: ClockPort;
  onEventLogEntry: (entry: CoordinatorEventLogEntry) => void;
  maxDepth: number;
  maxAgeMs: number;
  deferredMaxDepth: number;
};

export class OutboundCommandQueue {
  private readonly pendingQueue: OutboundBeaconCommand[] = [];
  private deferredQueue: OutboundBeaconCommand[] = [];

  constructor(private readonly dependencies: OutboundCommandQueueDependencies) {}

  enqueue(command: OutboundBeaconCommand, isDispatching: boolean): void {
    if (isDispatching) {
      this.removeExpiredCommands(this.deferredQueue, 'deferred');
      if (this.deferredQueue.length >= this.dependencies.deferredMaxDepth) {
        this.logOverflow(command, 'deferred', 'max-depth', 'incoming');
        return;
      }
      this.deferredQueue.push(command);
      this.logEnqueue(command, 'deferred');
      return;
    }

    this.removeExpiredCommands(this.pendingQueue, 'pending');
    if (this.pendingQueue.length >= this.dependencies.maxDepth) {
      const dropped = this.pendingQueue.shift();
      if (dropped) {
        this.logOverflow(dropped, 'pending', 'max-depth', 'oldest');
      }
    }
    this.pendingQueue.push(command);
    this.logEnqueue(command, 'pending');
  }

  takeNextDispatchableCommand(
    canDispatch: (command: OutboundBeaconCommand) => boolean
  ): OutboundBeaconCommand | undefined {
    this.removeExpiredCommands(this.pendingQueue, 'pending');
    const nextIndex = this.pendingQueue.findIndex(canDispatch);
    if (nextIndex === -1) {
      return undefined;
    }
    return this.pendingQueue.splice(nextIndex, 1)[0];
  }

  /**
   * Moves deferred commands into the pending queue after existing pending work.
   * FIFO is preserved within each flowId; inactive-flow commands may be skipped
   * by {@link takeNextDispatchableCommand} until their flow becomes dispatchable.
   */
  mergeDeferredToBack(): void {
    if (this.deferredQueue.length > 0) {
      this.pendingQueue.push(...this.deferredQueue);
      this.deferredQueue = [];
    }
  }

  dropPendingWhileNotReady(reason: string): void {
    if (this.pendingQueue.length === 0) {
      return;
    }
    this.pendingQueue.length = 0;
    this.dependencies.onEventLogEntry({ type: 'readiness-drop', meta: { reason } });
  }

  hasDispatchableCommand(canDispatch: (command: OutboundBeaconCommand) => boolean): boolean {
    return this.pendingQueue.some(canDispatch);
  }

  isEmpty(): boolean {
    return this.pendingQueue.length === 0 && this.deferredQueue.length === 0;
  }

  getQueueLengths(): { pendingQueue: number; deferredQueue: number } {
    return {
      pendingQueue: this.pendingQueue.length,
      deferredQueue: this.deferredQueue.length,
    };
  }

  private logEnqueue(command: OutboundBeaconCommand, queue: 'pending' | 'deferred'): void {
    this.dependencies.onEventLogEntry({
      type: 'enqueued',
      meta: {
        queue,
        dcr: command.dcr,
        id: command.id,
        source: command.source,
      },
    });
  }

  private logOverflow(
    command: OutboundBeaconCommand,
    queue: 'pending' | 'deferred',
    reason: 'max-depth' | 'max-age',
    dropped: 'incoming' | 'oldest'
  ): void {
    this.dependencies.onEventLogEntry({
      type: 'queue-overflow',
      meta: {
        queue,
        reason,
        dropped,
        id: command.id,
        dcr: command.dcr,
        source: command.source,
      },
    });
  }

  private removeExpiredCommands(
    queue: OutboundBeaconCommand[],
    queueName: 'pending' | 'deferred'
  ): void {
    const now = this.dependencies.clock.now();
    while (queue.length > 0) {
      const oldest = queue[0];
      if (!oldest || now - oldest.enqueuedAt <= this.dependencies.maxAgeMs) {
        break;
      }
      queue.shift();
      this.logOverflow(oldest, queueName, 'max-age', 'oldest');
    }
  }
}
